import { useState } from 'react';
import { CaretUp, CaretDown, Minus, Table as TableIcon } from '@phosphor-icons/react';
import { useMonthlyDeltas, type Platform, type MonthlyDeltaRow } from '@/api/ratings';

/**
 * MonthlyDeltasTable — tableau Delta mensuel par cadence (Stats Hub V2 PR #H).
 *
 * Reproduit le screenshot 3 du user : une ligne par mois (12 par defaut),
 * une colonne par cadence (3 pour FIDE/Lichess/chess.com selon la platform
 * selectionnee). Chaque cellule :
 *
 *  - Si delta > 0 : fleche CaretUp --volt-500 + chiffre.
 *  - Si delta < 0 : fleche CaretDown --coral-500 + chiffre.
 *  - Si delta = 0 : Minus --ink-300 + "0".
 *  - Si delta null (pas de games ce mois-ci) : tiret discret.
 *
 * Pas d'unite affichee (juste la valeur signee). Wording neutre.
 *
 * Switcher platform : 3 boutons (FIDE / Lichess / chess.com) au-dessus du tableau.
 *
 * Donnees : useMonthlyDeltas(platform, 12) -> endpoint
 * /users/me/ratings/monthly-deltas (PR #D, deja merge en main via PR #159).
 */

interface PlatformOption {
  id: Platform;
  label: string;
}

const PLATFORM_OPTIONS: ReadonlyArray<PlatformOption> = [
  { id: 'fide', label: 'FIDE' },
  { id: 'lichess', label: 'Lichess' },
  { id: 'chesscom', label: 'chess.com' },
];

const CADENCE_LABELS_BY_PLATFORM: Record<Platform, Record<string, string>> = {
  fide: { standard: 'Standard', rapide: 'Rapide', blitz: 'Blitz' },
  lichess: {
    classical: 'Classique',
    rapid: 'Rapide',
    blitz: 'Blitz',
    bullet: 'Bullet',
    puzzle: 'Puzzle',
    puzzle_rush: 'Puzzle Rush',
    storm: 'Storm',
  },
  chesscom: { rapid: 'Rapide', blitz: 'Blitz', bullet: 'Bullet', tactics: 'Tactics' },
};

const MONTH_NAMES_FR: ReadonlyArray<string> = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

export interface MonthlyDeltasTableProps {
  /** Platform initiale (default 'fide'). */
  defaultPlatform?: Platform;
  /** Nombre de mois (default 12, max 60). */
  months?: number;
}

export function MonthlyDeltasTable({
  defaultPlatform = 'fide',
  months = 12,
}: MonthlyDeltasTableProps): JSX.Element {
  const [platform, setPlatform] = useState<Platform>(defaultPlatform);
  const q = useMonthlyDeltas(platform, months);

  const cadences: ReadonlyArray<string> = q.data?.cadences ?? [];
  const rows: ReadonlyArray<MonthlyDeltaRow> = q.data?.rows ?? [];

  return (
    <div
      className="bg-ink-800 border border-ink-700 rounded-xl p-5"
      data-testid="monthly-deltas-table"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <TableIcon size={18} weight="regular" color="var(--paper-50)" aria-hidden="true" />
          <h3 className="text-heading-3 text-paper-50 m-0">Δ mensuel par cadence</h3>
        </div>
        <div
          className="flex gap-1 p-1 rounded-lg border border-ink-700 bg-ink-900"
          role="tablist"
          aria-label="Plateforme"
        >
          {PLATFORM_OPTIONS.map((p) => {
            const active = p.id === platform;
            return (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setPlatform(p.id)}
                className={`px-3 py-1 text-body-sm font-mono uppercase tracking-wider rounded-md transition-colors ${
                  active ? 'bg-acid-500 text-ink-900' : 'text-ink-300 hover:text-paper-50'
                }`}
                data-testid={`platform-${p.id}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </header>

      <p className="text-body-sm text-ink-300 italic mb-4">
        Variation de rating sur les {months} derniers mois (close mois M − close mois M−1).
      </p>

      {q.isLoading && (
        <p className="text-ink-300 font-mono text-body-sm py-6 text-center">Chargement…</p>
      )}

      {q.isError && !q.isLoading && (
        <p
          role="alert"
          className="text-coral-500 font-mono text-body-sm py-4"
          data-testid="monthly-deltas-error"
        >
          Impossible de charger les deltas mensuels.
        </p>
      )}

      {q.data && cadences.length > 0 && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table
            className="w-full border-collapse text-body-sm"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            <thead>
              <tr className="border-b border-ink-700">
                <th className="text-left py-2 px-3 text-ink-300 font-mono uppercase tracking-wider text-[11px]">
                  Période
                </th>
                {cadences.map((c) => (
                  <th
                    key={c}
                    className="text-right py-2 px-3 text-ink-300 font-mono uppercase tracking-wider text-[11px]"
                  >
                    {CADENCE_LABELS_BY_PLATFORM[platform][c] ?? c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.month}
                  className="border-b border-ink-700/50 hover:bg-ink-900/40 transition-colors"
                  data-testid={`row-${row.month}`}
                >
                  <td className="py-2 px-3 text-paper-50 whitespace-nowrap">
                    {formatMonthFr(row.month)}
                  </td>
                  {cadences.map((cad) => {
                    const key = `${cad}_delta` as keyof MonthlyDeltaRow;
                    const raw = row[key];
                    const delta = typeof raw === 'number' ? raw : null;
                    return (
                      <td key={cad} className="py-2 px-3 text-right">
                        <DeltaCell delta={delta} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {q.data && rows.length === 0 && !q.isLoading && (
        <p
          className="text-ink-300 italic text-body-sm py-6 text-center"
          data-testid="monthly-deltas-empty"
        >
          Pas encore assez d'historique pour calculer les deltas mensuels.
        </p>
      )}

    </div>
  );
}

function DeltaCell({ delta }: { delta: number | null }): JSX.Element {
  if (delta === null) {
    return (
      <span
        className="text-ink-300 font-mono opacity-50"
        data-testid="delta-empty"
        aria-label="Pas de partie ce mois-ci"
      >
        —
      </span>
    );
  }
  if (delta === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono text-ink-300"
        data-testid="delta-zero"
      >
        <Minus size={12} weight="bold" aria-hidden="true" />
        <span>0</span>
      </span>
    );
  }
  if (delta > 0) {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono"
        style={{ color: 'var(--volt-500)' }}
        data-testid="delta-positive"
        aria-label={`Progression de ${delta} points`}
      >
        <CaretUp size={12} weight="fill" aria-hidden="true" />
        <span>+{delta}</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 font-mono"
      style={{ color: 'var(--coral-500)' }}
      data-testid="delta-negative"
      aria-label={`Regression de ${Math.abs(delta)} points`}
    >
      <CaretDown size={12} weight="fill" aria-hidden="true" />
      <span>{delta}</span>
    </span>
  );
}

/**
 * Formate 'YYYY-MM' en "Mai 2026" (mois capitalise + annee). Leon parle FR.
 */
function formatMonthFr(yearMonth: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(yearMonth);
  if (!match) return yearMonth;
  const year = match[1];
  const monthNum = Number(match[2]);
  if (monthNum < 1 || monthNum > 12) return yearMonth;
  const name = MONTH_NAMES_FR[monthNum - 1] ?? yearMonth;
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`;
}
